import { loadAmplifyAuth } from "services/AuthRuntime";

const mutatingMethods = new Set(["POST", "PATCH", "DELETE", "PUT"]);
const consumedSnapStoreKeys = new Set();

const getRequestMethod = (options = {}) => String(options?.method || "GET").toUpperCase();

const getSnapStorePayload = (url, options = {}) => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const snapStore = window.snapStore;
  if (!snapStore || typeof snapStore !== "object") {
    return undefined;
  }

  if (getRequestMethod(options) !== "GET") {
    return undefined;
  }

  const authHeader = options?.headers?.Authorization;
  if (authHeader) {
    return undefined;
  }

  let resolvedUrl;
  try {
    resolvedUrl = new URL(url, window.location.origin);
  } catch (_error) {
    return undefined;
  }

  if (resolvedUrl.origin !== window.location.origin) {
    return undefined;
  }

  const key = `${resolvedUrl.pathname}${resolvedUrl.search}`;
  if (consumedSnapStoreKeys.has(key)) {
    return undefined;
  }

  if (!Object.prototype.hasOwnProperty.call(snapStore, key)) {
    return undefined;
  }

  consumedSnapStoreKeys.add(key);
  return snapStore[key];
};


const getHeaders = async (options) => {
  try {
    const { fetchAuthSession } = await loadAmplifyAuth();
    const session = await fetchAuthSession();
    options.headers = options.headers ?? {};
    options.headers.Accept = "application/json";
    options.headers.Authorization = session.tokens?.idToken?.toString();
    return options;
  } catch (e) {
    console.error("Can't get token", e);
    options.headers = options.headers ?? {};
    options.headers.Accept = "application/json";
    return options;
  }
};

function request(
  url,
  params = {},
  method,
) {
  const options = {
    method: method,
    credentials: "omit"
  };
  if ("GET" === method && !!params && Object.keys(params).length > 0) {
    url += "?" + new URLSearchParams(params).toString();
  } else if (mutatingMethods.has(method)) {
    options.headers = {
      ...(options.headers ?? {}),
      "Content-Type": "application/json",
    };
    options.body = JSON.stringify(params);
  }
  // console.log(url, options);
  return getHeaders(options).then(options => {
    // console.log("auth options: ", options);
    return sendRequest(url, options);
  });
}

export function sendRequest(url, options) {
  const snapStorePayload = getSnapStorePayload(url, options);
  if (snapStorePayload !== undefined) {
    return Promise.resolve(snapStorePayload);
  }

  return fetch(url, options)
    .then(async (response) => {
      const contentType = response.headers.get("content-type") || "";

      if (!response.ok) {
        const errorBody = await response.text().catch(() => "");
        const error = new Error(`Request failed with status ${response.status} for ${url}`);
        error.status = response.status;
        error.url = url;
        error.body = errorBody;
        throw error;
      }

      if (response.status === 204) {
        return null;
      }

      if (!contentType.toLowerCase().includes("application/json")) {
        const body = await response.text().catch(() => "");
        const preview = body.slice(0, 120).trim();
        const error = new Error(`Expected JSON response from ${url}, received ${contentType || "unknown content type"}${preview ? `: ${preview}` : ""}`);
        error.status = response.status;
        error.url = url;
        error.body = body;
        throw error;
      }

      return response.json();
    })
    .catch((err) => {
      console.error("catch request", err);
      return Promise.reject(err);
    });
}

export function get(url, params) {
  return request(url, params, "GET");
} 

export function post(url, body) {
  return request(url, body, "POST");
}

export function remove(url, params) {
  return request(url, params, "DELETE");
}

export function patch(url, body) {
  return request(url, body, "PATCH");
}
